import { ua } from './ua';
import { isAndroid, isIos } from './system';
import { isUmuWx } from './umu';

/**
 * 是否飞书桌面版
 * [手机端]Mozilla/5.0 (Linux; Android 9; MI 6X Build/PKQ1.180904.001; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/75.0.3770.156 Mobile Safari/537.36 Lark/5.10.4 LarkLocale/zh_CN ChannelName/Feishu TTWebView/0751130016452
 * [桌面端]Mozilla/5.0 (Macintosh; Intel Mac OS X 11_6_1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/101.0.4951.53 Safari/537.36 Lark/5.32.6 LarkLocale/zh_CN Electron/Native WebApp/workplace
 */
export const isFeishu = /Lark/i.test(ua) || /Feishu/i.test(ua);
export const isFeiShuMobile = isFeishu && /Mobile/i.test(ua);
export const isFeiShuDesktop = /Electron/i.test(ua) && (/Lark/i.test(ua) || /Feishu/i.test(ua));
/**
 * 是否在钉钉桌面版 Web环境内
 */
export const isDingdingDesktop = !/AliApp/i.test(ua) && /DingTalk/i.test(ua);

export const isIpad = /iPad/i.test(ua);

/**
 * 鸿蒙手机/平板端（不含鸿蒙 PC）：UA 含 HarmonyOS/OpenHarmony 且设备类型非 PC
 */
const isHarmonyOSMobile =
    /HarmonyOS|OpenHarmony/i.test(ua) && !/\(PC\s*;/i.test(ua);

/**
 * 是否移动端
 */
export const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    isHarmonyOSMobile ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints > 1 || 'ontouchstart' in window)) ||
    isDingdingDesktop; // iPadOS13

/**
 * 是否手机端（不包括iPad）
 */
export const isPhone = isMobile && !isIpad;

/**
 * 是否桌面端浏览器
 */
export const isDesktop = !isMobile || isDingdingDesktop;

/**
 * 是否在钉钉App Web环境内
 */
export const isDingding = /AliApp/i.test(ua) && /DingTalk/i.test(ua);

/**
 * 是否为QQ App内Web浏览器
 */
export const isQQ = /TBS/i.test(ua);

/**
 * 是否为iOS平台下的
 */
export const isIosQQ = isIos && / QQ/i.test(ua);

/**
 * 是否为QQ浏览器
 */
export const isQQBrowser = /QQBrowser/i.test(ua);

/**
 * 是否为Safari浏览器
 */
export const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);

/**
 * 是否为Sogou浏览器
 */
export const isSogou = /MetaSr/i.test(ua);

/**
 * 是否为Samsung浏览器
 */
export const isSamsungBrowser = /SamsungBrowser/i.test(ua);

/**
 * 是否为小米浏览器
 */
export const isMiuiBrowser = /MiuiBrowser/i.test(ua);

/**
 * 是否为HeyTapBrowser
 */
export const isHeyTapBrowser = /HeyTapBrowser/i.test(ua);

/**
 * 是否为MobileLenovoBrowser
 */
export const isMobileLenovoBrowser = /MobileLenovoBrowser/i.test(ua);

/**
 * 是否为PinganZhiniao的webview
 */
export const isPinganZhiniaoWebview = /PinganZhiniao/i.test(ua);

/**
 * 是否为Chrome浏览器
 */
export const isChrome = /Chrome/i.test(ua);

/**
 * 是否为iOS上的Chrome浏览器
 */
export const isCriOS = /CriOS/i.test(ua);

/**
 * 是否为Edge浏览器【Chrome内核】
 */
export const isChromeBasedEdge = /edg\//i.test(ua);

/**
 * 获取Chrome浏览器版本
 * @returns Chrome浏览器版本号
 */
export const getChromeVersion = () => {
    const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
    return raw ? parseInt(raw[2], 10) : false;
};

export const chromeVersion = getChromeVersion();

/**
 * 是否移动端Web浏览器
 */
export const isMobileBrowser = !isUmuWx && (isAndroid || isIos);

/**
 * @description: 是否为 Firefox 浏览器
 */

export const isFirefox = ua.indexOf('firefox') > -1;

/**
 * 是否为Edge浏览器
 */
export const isEdge = /Edg|Edge/.test(ua);

export function getBrowserName() {
    const ua = navigator.userAgent.toLowerCase();

    if (ua.indexOf('opera') > -1 || ua.indexOf('opr') > -1) {
        return 'Opera';
    } else if (ua.indexOf('edge') > -1 || ua.indexOf('edg') > -1) {
        return 'Edge';
    } else if (ua.indexOf('firefox') > -1) {
        return 'Firefox';
    } else if (ua.indexOf('safari') > -1 && ua.indexOf('chrome') == -1) {
        return 'Safari';
    } else if (ua.indexOf('chrome') > -1 && ua.indexOf('safari') > -1) {
        return 'Chrome';
    } else if (ua.indexOf('qqbrowser') > -1) {
        return 'QQBrowser';
    } else if (ua.indexOf('metasr') > -1) {
        return 'SogouBrowser';
    } else if (ua.indexOf('samsungbrowser') > -1) {
        return 'SamsungBrowser';
    } else if (ua.indexOf('miuibrowser') > -1) {
        return 'MiuiBrowser';
    } else {
        return '';
    }
}

export function normalizeVersion(version: number | string) {
    if (typeof version === 'number') {
        return `${version}.0.0`;
    }
    return version;
}

export function getBrowserVersion(browser: string) {
    let match = null;
    const defaultVersion = '0.0.0';
    const ua = navigator.userAgent.toLowerCase();

    switch (browser) {
        case 'safari':
            match = ua.match(/version\/([\d\.]+).*safari/);
            return match ? match?.[1] : defaultVersion;
        case 'chrome':
            match = ua.match(/chrom(e|ium)\/(\d+)\./);
            const version = match ? parseInt(match?.[2], 10) : defaultVersion;
            return normalizeVersion(version);
        case 'firefox':
            match = ua.match(/firefox\/([\d\.]+)/);
            return match ? match?.[1] : defaultVersion;
        case 'edge':
            match = ua.match(/edge?\/([\d\.]+)/);
            return match ? match?.[1] : defaultVersion;
        case 'opera':
            match = ua.match(/(?:opera|opr).([\d\.]+)/);
            return match ? match?.[1] : defaultVersion;
        default:
            return defaultVersion;
    }
}

export function getBrowserType() {
    return isMobile ? 'mobile' : 'pc';
}

export function compareBrowserVersion(version1: string, version2: string) {
    const v1 = version1.split('.');
    const v2 = version2.split('.');
    for (let i = 0; i < v1.length || i < v2.length; i++) {
        let x = 0;
        let y = 0;
        if (i < v1.length) {
            x = parseInt(v1[i]);
        }
        if (i < v2.length) {
            y = parseInt(v2[i]);
        }
        if (x > y) {
            return 1;
        }
        if (x < y) {
            return -1;
        }
    }
    return 0;
}

export function isInMicroApp() {
    return !!(window as any).microApp;
}

export function MicroAppInstance() {
    return (window as any).microApp;
}

/**
 * 只用来甄别window10 和 window11 其他版本暂时不做处理
 * @returns Promise<string> Windows版本信息
 */
export async function getWindowsOperateSystemPlatform(): Promise<string> {
    const windowNavigator = navigator as any;
    try {
        // 使用 User-Agent Client Hints API 获取更准确的信息
        const ua = await windowNavigator?.userAgentData?.getHighEntropyValues?.(['platformVersion', 'platform']);

        if (windowNavigator?.userAgentData?.platform === 'Windows') {
            const platformVersion = ua?.platformVersion || '';
            const versionParts = platformVersion.split('.') || [];
            const majorVersion = parseInt(versionParts?.[0]);
            const minorVersion = parseInt(versionParts?.[1] || '0');

            // Windows 11 的检测逻辑
            if (majorVersion >= 13) {
                return 'Windows 11';
            } else if (majorVersion === 10) {
                // Windows NT 10.0 可能是 Windows 10 或 Windows 11
                // 通过其他特征来区分
                if (minorVersion >= 22000) {
                    return 'Windows 11';
                } else {
                    return 'Windows 10';
                }
            }
            return '';
        } else {
            return '';
        }
    } catch (_error) {
        return '';
    }
}
interface IInfo {
    showWindow?: boolean;
}

const defaultInfo: IInfo = {
    showWindow: true,
};

/**
 * 获取操作系统平台版本
 * @param info 配置信息
 * @returns Promise<string> 操作系统版本信息
 */
export const getOperateSystemPlatformVersion = async (info: IInfo = defaultInfo): Promise<string> => {
    const { showWindow = true } = info;

    if (!showWindow) {
        return '';
    }

    try {
        return await getWindowsOperateSystemPlatform();
    } catch (_error) {
        return '';
    }
};

export const isPixel = /Pixel/i.test(ua);
export const isSharp = /AQUOS/i.test(ua);
export const isSony = /Xperia/i.test(ua);
/**
 * 是否为 WKWebView（App 内嵌浏览器）
 * 排除 Safari 和已知的独立浏览器应用
 */
export const isWKWebView =
    /AppleWebKit/.test(ua) &&
    !isSafari &&
    !/CriOS/i.test(ua) &&      // 排除 iOS Chrome
    !/FxiOS/i.test(ua) &&      // 排除 iOS Firefox
    !/OPiOS/i.test(ua) &&      // 排除 iOS Opera
    !/EdgiOS/i.test(ua) &&     // 排除 iOS Edge
    !/Safari\//i.test(ua);     // WKWebView 通常不含此标识
